
import React from "react";
import Partners from "./home/Partners";
import ShopRow from "./home/ShopRow";
import SquadGrid from "./home/SquadGrid";
import GalleryRow from "./home/GalleryRow";
import HeroSlider from "./home/HeroSlider";
import EventSlider from "./home/EventSlider";
import FeatureCards from "./home/FeatureCards";
import { isPageVisible } from "../helpers";
import MemberModal from "./common/MemberModal";
import UpcomingMatches from "./home/UpcomingMatches";
import { BaseService, IGallery, ITeamMember, ITeamNews, ITeamWebsite, ITournamentEdition, ITournamentEditionMatch, OmneHostService, SanitizedResponse } from "../../../../services";

export interface HomeProps {
    basePath: string;
    publicId: string;
    website: ITeamWebsite;
}

function Home({ website, basePath, publicId }: HomeProps) {

    const settings = website.home_settings ?? {} as ITeamWebsite["home_settings"];
    const [news, setNews] = React.useState<ITeamNews[]>();
    const [gallery, setGallery] = React.useState<IGallery[]>();
    const [members, setMembers] = React.useState<ITeamMember[]>();
    const [matches, setMatches] = React.useState<ITournamentEditionMatch[]>();
    const [tournaments, setTournaments] = React.useState<ITournamentEdition[]>();
    const [selectedMember, setSelectedMember] = React.useState<ITeamMember | null>(null);
    const showSchedule = isPageVisible(website, "SCHEDULE") && settings.show_schedules !== false;
    const showGallery = isPageVisible(website, "GALLERY") && settings.show_top_gallery !== false;

    React.useEffect(fetchInformation, [website.team.public_id]);

    return (<>
        <HeroSlider website={website} basePath={basePath} news={news} />
        {showSchedule && (<UpcomingMatches website={website} basePath={basePath} publicId={publicId} matches={matches} />)}
        <FeatureCards website={website} basePath={basePath} news={news} tournaments={tournaments} />
        {(isPageVisible(website, "MEMBERS") && !!members?.length) && (<SquadGrid website={website} basePath={basePath} members={members} onSelect={setSelectedMember} />)}
        {isPageVisible(website, "MERCH") && (<ShopRow website={website} basePath={basePath} />)}
        {!!tournaments?.length && (<EventSlider tournaments={tournaments} />)}
        {(showGallery && !!gallery?.length) && (<GalleryRow basePath={basePath} gallery={gallery} />)}
        {(settings.show_sponsors && !!website.sponsors?.length) && (<Partners sponsors={website.sponsors} />)}
        {selectedMember && (<MemberModal member={selectedMember} onClose={() => setSelectedMember(null)} />)}
    </>);

    function fetchInformation() {
        const omnehostService = OmneHostService.getInstance();
        const teamId = website.team.public_id;
        omnehostService.queryTeamNews(teamId, {
            size: 5,
            sort: "updated_at,DESC",
        }).then(({ content }: SanitizedResponse<ITeamNews>) => {
            setNews(content ?? []);
        }).catch(BaseService.reportError);
        if (showSchedule) {
            omnehostService.queryTeamSchedule(teamId, {
                size: 10,
                sort: ["start_date,ASC", "status,ASC"],
            }).then(({ content }: SanitizedResponse<ITournamentEditionMatch>) => {
                setMatches(content ?? []);
            }).catch(BaseService.reportError);
        }
        if (isPageVisible(website, "MEMBERS")) {
            omnehostService.queryTeamMembers(teamId, {
                size: 12,
                status: "ACTIVE",
                sort: "role,DESC",
            }).then(({ content }: SanitizedResponse<ITeamMember>) => {
                setMembers(content ?? []);
            }).catch(BaseService.reportError);
        }
        omnehostService.queryTeamTournaments(teamId, {
            page: 1,
            size: 5,
            status: "ACTIVE",
        }).then(({ content }: SanitizedResponse<ITournamentEdition>) => {
            setTournaments(content ?? []);
        }).catch(BaseService.reportError);
        if (showGallery) {
            omnehostService.queryTeamGallery(teamId, {
                size: 6,
                sort: "updated_at,DESC",
            }).then(({ content }: SanitizedResponse<IGallery>) => {
                setGallery(content ?? []);
            }).catch(BaseService.reportError);
        }
    }
}

export default Home;
